import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button, Input, Checkbox } from '@components';

export type PromptArchitectQuestionType = 'text' | 'single' | 'multi';

export interface PromptArchitectQuestion {
  id: string;
  label: string;
  type: PromptArchitectQuestionType;
  options?: string[];
  placeholder?: string;
}

interface QuestionsOverlayProps {
  isOpen: boolean;
  questions: PromptArchitectQuestion[];
  onClose: () => void;
  /** Called with a human-readable answer summary to send back into the chat. */
  onSubmit: (answerText: string) => void;
}

type AnswerValue = string | string[];

/** Wizard-style overlay that renders AI follow-up questions as a form. */
export const QuestionsOverlay: React.FC<QuestionsOverlayProps> = ({
  isOpen,
  questions,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [answers, setAnswers] = useState<Record<string, AnswerValue>>({});

  // Reset answers whenever a new set of questions is shown.
  const questionsKey = useMemo(
    () => questions.map(q => q.id).join('|'),
    [questions]
  );
  React.useEffect(() => {
    setAnswers({});
  }, [questionsKey]);

  const setText = (id: string, value: string) => {
    setAnswers(prev => ({ ...prev, [id]: value }));
  };

  const setSingle = (id: string, value: string) => {
    setAnswers(prev => ({ ...prev, [id]: value }));
  };

  const toggleMulti = (id: string, value: string) => {
    setAnswers(prev => {
      const current = Array.isArray(prev[id]) ? (prev[id] as string[]) : [];
      const next = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      return { ...prev, [id]: next };
    });
  };

  const buildAnswerText = (): string =>
    questions
      .map(q => {
        const raw = answers[q.id];
        let value = '';
        if (Array.isArray(raw)) {
          value = raw.join(', ');
        } else if (typeof raw === 'string') {
          value = raw.trim();
        }
        if (!value) return null;
        return `${q.label}\n→ ${value}`;
      })
      .filter(Boolean)
      .join('\n\n');

  const hasAnyAnswer = questions.some(q => {
    const raw = answers[q.id];
    return Array.isArray(raw) ? raw.length > 0 : Boolean((raw || '').toString().trim());
  });

  const handleSubmit = () => {
    const text = buildAnswerText();
    if (!text) return;
    onSubmit(text);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('agents.promptArchitect.questionsTitle')}
      size="lg"
    >
      <div className="flex max-h-[70vh] flex-col">
        <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">
          {t('agents.promptArchitect.questionsHint')}
        </p>

        <div className="flex-1 space-y-6 overflow-y-auto pr-1">
          {questions.map(q => (
            <div key={q.id}>
              <p className="mb-2 text-sm font-medium text-gray-800 dark:text-gray-100">
                {q.label}
              </p>

              {q.type === 'text' && (
                <Input
                  fullWidth
                  value={(answers[q.id] as string) || ''}
                  placeholder={q.placeholder}
                  onChange={e => setText(q.id, e.target.value)}
                />
              )}

              {q.type === 'single' && (
                <div className="flex flex-wrap gap-2">
                  {(q.options || []).map(opt => {
                    const selected = answers[q.id] === opt;
                    return (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setSingle(q.id, opt)}
                        className={
                          selected
                            ? 'rounded-full border border-primary-500 bg-primary-500 px-4 py-1.5 text-sm text-white'
                            : 'rounded-full border border-gray-300 bg-white px-4 py-1.5 text-sm text-gray-700 hover:border-primary-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200'
                        }
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              )}

              {q.type === 'multi' && (
                <div className="space-y-2">
                  {(q.options || []).map(opt => {
                    const current = Array.isArray(answers[q.id])
                      ? (answers[q.id] as string[])
                      : [];
                    return (
                      <Checkbox
                        key={opt}
                        label={opt}
                        checked={current.includes(opt)}
                        onChange={() => toggleMulti(q.id, opt)}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-6 flex justify-end gap-2 border-t border-gray-200 pt-4 dark:border-gray-700">
          <Button variant="secondary" onClick={onClose}>
            {t('agents.promptArchitect.questionsSkip')}
          </Button>
          <Button
            variant="gradient-multi"
            onClick={handleSubmit}
            disabled={!hasAnyAnswer}
          >
            {t('agents.promptArchitect.questionsSubmit')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default QuestionsOverlay;
